// Pluggable client-side error reporter (S29.5).
//
// Replaces the `console.error('[ErrorBoundary]', ...)` placeholder
// in components/ErrorBoundary.tsx with a reporter that's pluggable
// at runtime. The codebase has no Sentry SDK dependency yet; this
// module is the seam an operator wires up when they pick a tracker
// (Sentry, Bugsnag, in-house) without forcing a hard dep on the
// scaffold.
//
// Resolution order (each fires if its prerequisite is present;
// unconditional console.error always fires last so dev output
// never goes silent):
//
//   1. window.Sentry.captureException — if the Sentry SDK has
//      been loaded (e.g. via a <script> tag in index.html or a
//      future @sentry/react migration), this picks it up
//      automatically. No build-time import required.
//   2. POST to VITE_ERROR_REPORT_URL — when set, the reporter
//      sends a small JSON envelope to that endpoint. Fire-and-
//      forget; we don't surface upload failures because we're
//      already in a bad state.
//   3. console.error — always, last.

interface ReporterContext {
  /** Component stack from React's componentDidCatch, when available. */
  componentStack?: string
  /** Free-form key/value tags. Keep payload size small — this is
   *  serialised into a beacon. */
  tags?: Record<string, string>
}

interface SentryGlobal {
  captureException?: (err: unknown, hint?: { extra?: ReporterContext }) => void
}

/** reportError is the single seam every component / hook should
 *  call when catching a render-time exception. */
export function reportError(error: unknown, context: ReporterContext = {}): void {
  // 1. Sentry — runtime-loaded SDK. Guarded check so a missing
  //    SDK is a no-op rather than a TypeError.
  try {
    const sentry = (globalThis as { Sentry?: SentryGlobal }).Sentry
    if (sentry?.captureException) {
      sentry.captureException(error, { extra: context })
    }
  } catch {
    // Reporter must NEVER throw — swallow.
  }

  // 2. POST endpoint. Uses sendBeacon when available (works during
  //    page unload), else falls back to fetch with keepalive.
  try {
    const url = (import.meta.env.VITE_ERROR_REPORT_URL as string | undefined)?.trim()
    if (url) {
      const payload = JSON.stringify({
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : undefined,
        stack: error instanceof Error ? error.stack : undefined,
        componentStack: context.componentStack,
        tags: context.tags,
        href: typeof window !== 'undefined' ? window.location.href : undefined,
        timestamp: new Date().toISOString(),
      })
      const beacon =
        typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
          ? navigator.sendBeacon.bind(navigator)
          : null
      if (beacon) {
        beacon(url, new Blob([payload], { type: 'application/json' }))
      } else {
        // keepalive ensures the request survives an unload event.
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {})
      }
    }
  } catch {
    // Reporter must NEVER throw — swallow.
  }

  // 3. Console — always last so devs always see something.

  console.error('[reportError]', error, context)
}
